import { useCallback, useMemo, useState } from 'react';
import ReactAsyncSelect from 'react-select-new/async';

import { colors } from '@anyroad/theme';

import {
  getSelectStyles,
  InputError,
  InputErrorWrapper,
  Label,
  RightIcon,
  SelectWrapper,
  Subtext,
} from './styles';
import type { LocationOption } from './styles';

export type { LocationOption } from './styles';

export type ValidationState = 'idle' | 'invalid' | 'valid';

export interface AsyncSelectProps {
  classNamePrefix?: string;
  errorMessage?: string;
  label: string;
  loadOptions(value: string): Promise<LocationOption[]>;
  minInputLength?: number;
  onChange?: (value: string) => void;
  placeholder: string;
  subtext: string;
  validationState?: ValidationState;
  value?: LocationOption | null;
}

export const AsyncSelect = ({
  classNamePrefix,
  errorMessage,
  label,
  loadOptions,
  minInputLength = 3,
  placeholder,
  subtext,
  onChange,
  validationState: validationStateProp,
  value: valueProp,
}: AsyncSelectProps) => {
  const [inputValue, setInputValue] = useState('');
  const [internalSelected, setInternalSelected] = useState<LocationOption | null>(null);
  const selected = valueProp !== undefined ? valueProp : internalSelected;
  const [internalValidationState, setInternalValidationState] =
    useState<ValidationState>('idle');

  const validationState = validationStateProp ?? internalValidationState;

  const styles = useMemo(() => getSelectStyles(), []);

  const handleBlur = useCallback(() => {
    if (validationStateProp !== undefined) return;
    const q = (selected?.label ?? inputValue).trim();
    if (!q) {
      setInternalValidationState('idle');
      return;
    }
    const looksValid = /\d/u.test(q) && /[a-zA-Z]/u.test(q);
    setInternalValidationState(looksValid ? 'valid' : 'invalid');
  }, [selected?.label, inputValue, validationStateProp]);

  const handleChange = useCallback(
    (opt: LocationOption | null) => {
      onChange?.(opt?.value ?? '');
      setInternalSelected(opt ?? null);
      setInputValue('');
      if (validationStateProp === undefined) {
        setInternalValidationState(opt ? 'valid' : 'idle');
      }
    },
    [onChange, validationStateProp]
  );

  const handleInputChange = useCallback(
    (val: string, meta: { action: string }) => {
      if (meta.action === 'input-change') {
        onChange?.(val ?? '');
        setInternalSelected(null);
        setInputValue(val);
        if (validationStateProp === undefined) {
          setInternalValidationState('idle');
        }
      }
      return val;
    },
    [onChange, validationStateProp]
  );

  const icon =
    validationState === 'valid'
      ? 'OutlinedCheck'
      : validationState === 'invalid'
        ? 'Warning'
        : undefined;

  const iconFill =
    validationState === 'valid'
      ? colors.success
      : validationState === 'invalid'
        ? colors.warningOrange
        : undefined;

  return (
    <div style={{ width: '100%' }}>
      <Label>{label}</Label>
      <InputErrorWrapper>
        <SelectWrapper>
          <ReactAsyncSelect<LocationOption, false>
            aria-invalid={errorMessage ? 'true' : 'false'}
            aria-label={label}
            cacheOptions
            classNamePrefix={classNamePrefix}
            components={{ DropdownIndicator: null, IndicatorSeparator: null }}
            defaultOptions={false}
            inputValue={inputValue}
            loadOptions={(value) => {
              const q = value.trim();
              if (q.length < minInputLength) return Promise.resolve([]);
              return loadOptions(q);
            }}
            menuPortalTarget={
              typeof document !== 'undefined' ? document.body : undefined
            }
            menuPosition="fixed"
            onBlur={handleBlur}
            onChange={handleChange}
            onInputChange={handleInputChange}
            placeholder={placeholder}
            styles={styles}
            value={selected}
          />
          {icon ? (
            <RightIcon fill={iconFill} height="16px" name={icon} width="16px" />
          ) : null}
        </SelectWrapper>
        {Boolean(errorMessage) && <InputError>{errorMessage}</InputError>}
      </InputErrorWrapper>
      <Subtext>{subtext}</Subtext>
    </div>
  );
};
